import React from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import { ChartLegend } from '../../../../../../components/chart-legend/chart-legend';
import { Header } from '../../../../../../components/header';
import { heatmapColors } from '../utils';

const LegendContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--color-element)',
  color: 'var(--color-type-primary)',
  padding: '16px',
  marginBottom: '24px',
  borderRadius: '8px',
});

const ColorBar = styled('div')({
  display: 'flex',
  width: '100%',
  height: '24px',
  overflow: 'hidden',
  borderRadius: '9999px',
});

const ColorSegment = styled('div')(({ color, rounded }: { color: string; rounded: string }) => ({
  flex: 1,
  backgroundColor: color,
  borderTopLeftRadius: rounded === 'left' ? '9999px' : 0,
  borderBottomLeftRadius: rounded === 'left' ? '9999px' : 0,
  borderTopRightRadius: rounded === 'right' ? '9999px' : 0,
  borderBottomRightRadius: rounded === 'right' ? '9999px' : 0,
}));

type HeatmapLegendProps = {
  ranges?: string[];
  subTitle?: string;
  hideHeader?: boolean;
  noPadding?: boolean;
};

export const HeatmapLegend: React.FC<HeatmapLegendProps> = ({ ranges, subTitle, hideHeader, noPadding }) => {
  const { t } = useTranslation('channel-details');
  const colorBarShouldBeVisible = ranges ? ranges.length > 3 : true;

  const labels = [
    { key: 'limitUsage', color: heatmapColors[6] },
    { key: 'neutralRates', color: heatmapColors[3] },
    { key: 'useEnergy', color: heatmapColors[0] },
  ];

  const legenData = labels
    .filter((x) => (ranges ? ranges.includes(x.color) : true))
    .map((x) => ({ label: t(`optimizer.configuration.tariff.${x.key}`), color: x.color }));

  return (
    <LegendContainer {...(noPadding && { sx: { padding: 0 } })}>
      {!hideHeader && (
        <Header
          title={t('optimizer.configuration.tariff.legend')}
          {...(!colorBarShouldBeVisible && { className: 'm-b-0' })}
          {...(subTitle && { subtitle: subTitle, column: true, subheader: true })}
        />
      )}

      {colorBarShouldBeVisible && (
        <ColorBar>
          {Object.values(heatmapColors)
            .reverse()
            .filter((color) => (!ranges ? true : ranges.includes(color)))
            .map((color, index) => (
              <ColorSegment
                key={index}
                color={color}
                rounded={index === 0 ? 'left' : index === Object.values(heatmapColors).length - 1 ? 'right' : ''}
              />
            ))}
        </ColorBar>
      )}
      <ChartLegend
        items={legenData}
        dotSize={16}
        fontSize={14}
        {...(colorBarShouldBeVisible && { spaceBetween: true })}
        {...((colorBarShouldBeVisible || !hideHeader) && { className: 'm-t-16' })}
      />
    </LegendContainer>
  );
};
